import React, { useState, useRef, useEffect } from 'react'

import { HtmlContent, Input, Typography, makeStyles, useIsTruncated } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { Placeholder, PresEditorTooltip } from '../../components'
import { useDebouncedCommit } from '../../util/useDebouncedCommit'
import CodeIcon from '@material-ui/icons/Code'
import CreateIcon from '@material-ui/icons/Create'
import classnames from 'classnames'

interface InlineEditableTextProps {
  children: string
  classes?: StyleClasses<typeof useStyles>
  handleEdit: (content: string) => void
  hasIcon?: boolean
  hasPreventEmpty?: boolean
  // the height prop ensures Input and Typography have the same height for isInSitu, assumes multiline is false
  height?: number
  isInSitu?: boolean
  isSubmitOnEnter?: boolean
  maxLength?: number
  multiline?: boolean
  placeholderButtonText?: string
  renderHtml?: boolean
  tooltipTitle?: string
  /**
   * Which gesture starts editing. Defaults to 'click'. 'doubleClick' lets a single click bubble to a
   * parent selection handler instead of starting an edit — used by the global footer (#22211), where
   * a single click selects the footer and only a double-click edits a field.
   */
  triggerOn?: 'click' | 'doubleClick'
  variant?: string
}

// isInSitu edit-mode input width (see inputWidth below): never let the box collapse to a
// sliver on a short/empty title, and leave a small buffer past the exact character count so
// the caret and its blink aren't flush against the border.
const MIN_INPUT_WIDTH_CH = 4
const INPUT_WIDTH_PADDING_CH = 2

const useStyles = makeStyles<Theme, InlineEditableTextProps>(
  (theme: Theme) => {
    return {
      root: {},
      editableWrapper: {
        cursor: 'pointer',
        minWidth: 0,
        alignSelf: 'stretch'
      },
      editableBlock: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        '&:hover': {
          '& $editableIcon': {
            fill: theme.palette.primary.main
          }
        }
      },
      editableIcon: {
        marginLeft: theme.spacing(1),
        fill: theme.palette.text.primary,
        fontSize: '1rem'
      },
      inputRoot: (props) => ({
        ...(props.isInSitu && {
          border: 'none',
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.borderRadius['sm']
        })
      }),
      input: (props) => {
        const variantStyle =
          props.variant && typeof theme.typography[props.variant] === 'object'
            ? theme.typography[props.variant]
            : undefined
        return {
          ...variantStyle,
          ...(props.isInSitu && {
            padding: 0,
            minHeight: 'auto'
          }),
          // Floored at 16px regardless of the variant's own fontSize: below this, iOS Safari
          // auto-zooms the viewport on focus and `user-scalable=no` blocks zooming back out.
          // max() preserves a larger heading size instead of capping every variant to 16px.
          fontSize: `max(16px, ${variantStyle?.fontSize || '1rem'})`
        }
      },
      height: (props) => ({
        height: props.height
      }),
      hasError: {
        outline: `1px solid ${theme.palette.error.main}`
      },
      typogText: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        display: 'block',
        minWidth: 0
      }
    }
  },
  { name: 'InlineEditableText' }
)

export default function InlineEditableText(props: InlineEditableTextProps) {
  const classes = useStyles(props)
  const {
    children,
    handleEdit,
    hasIcon,
    hasPreventEmpty,
    height,
    isInSitu,
    isSubmitOnEnter,
    maxLength,
    multiline,
    placeholderButtonText,
    renderHtml,
    tooltipTitle,
    triggerOn = 'click',
    variant
  } = props
  const [editing, setEditing] = useState(false)
  const [content, setContent] = useState(children)

  // The debounced commit (below) round-trips whatever we just typed back down through Formik into
  // `children` a moment later — this ref lets the sync effect tell that echo apart from a real
  // external change. Only a value we did NOT ourselves just commit should reset `content`. See
  // InlineEditableHtml's identical guard for the full rationale (#23829, #23885).
  const lastCommittedRef = useRef(children)

  useEffect(() => {
    if (children === lastCommittedRef.current) return
    setContent(children)
  }, [children])
  const [hasError, setHasError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { ref: truncateRef, isTruncated } = useIsTruncated<HTMLSpanElement>(content)

  // isInSitu fields (currently just the presentation title) have no width of their own —
  // the wrapping elements all size to content, so with nothing set here the native <input>
  // falls back to the browser's default form-control width (~20 characters) regardless of
  // how long the value actually is, clipping/auto-scrolling anything longer than that.
  // Sizing to the live content in `ch` (not a fixed maxLength-wide box) keeps it matching
  // the display state's shrink-to-fit Typography, so there's no jump switching in/out of
  // edit mode. Recomputed on every render from `content`, which already re-renders on every
  // keystroke — not memoised, since the computation itself is trivially cheap.
  const inputWidth = isInSitu
    ? `${Math.max((content ?? '').length, MIN_INPUT_WIDTH_CH) + INPUT_WIDTH_PADDING_CH}ch`
    : undefined

  // Both commit paths go through this so `lastCommittedRef` always reflects the latest value we
  // ourselves wrote, whether it landed via the debounce or via blur.
  const commit = (value: string) => {
    lastCommittedRef.current = value
    handleEdit(value)
  }

  // Typing commits on a debounce as well, so the form is dirty (and Save is clickable) while the
  // field still has focus — see useDebouncedCommit for why. The blur commit below stays: it is what
  // guarantees the final value lands, whatever the timer was doing.
  const draftCommit = useDebouncedCommit(commit)

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = ev.target.value
    // Block growth past maxLength instead of relying on the native `maxlength` attribute: once a
    // title imported/converted from elsewhere is already over the cap, the native attribute
    // silently eats every keystroke with no feedback (ENG-1659). Only growth is blocked — a title
    // already over the cap can still be edited shorter, just not made longer while over it.
    if (maxLength && value.length > maxLength && value.length > content.length) {
      setHasError(true)
      return
    }
    setContent(value)
    if (value.length > 0 && hasError) {
      setHasError(false)
    }
    // An empty value is never committed early: `hasPreventEmpty` fields reject it on blur anyway,
    // and committing '' mid-edit would blank the title on screen while the user is clearing it to
    // retype.
    if (ev.target.value || !hasPreventEmpty) {
      draftCommit.schedule(ev.target.value)
    }
  }

  const handleBlur = (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!content && hasPreventEmpty) {
      setHasError(true)
      return (inputRef.current.firstChild as HTMLElement)?.focus()
    }
    if (ev) ev.stopPropagation()
    // Drop any scheduled commit first — it holds the same content this is about to write.
    draftCommit.cancel()
    setEditing(false)
    commit(content)
  }

  const handleKeyPress = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    if (isSubmitOnEnter && ev.key === 'Enter') {
      ev.preventDefault()
      handleBlur()
    }
  }

  return (
    <>
      <div
        onClick={triggerOn === 'doubleClick' ? undefined : () => setEditing(true)}
        onDoubleClick={triggerOn === 'doubleClick' ? () => setEditing(true) : undefined}
        className={classnames(classes.root, { [classes.editableWrapper]: !editing })}
      >
        {editing && (
          <>
            <Input
              autoFocus
              placeholder={hasError && !content ? 'Please enter a title' : ''}
              classes={{
                root: classnames(classes.inputRoot, { [classes.height]: height, [classes.hasError]: hasError }),
                input: classes.input
              }}
              inputProps={{ style: inputWidth ? { width: inputWidth } : undefined }}
              multiline={isInSitu ? false : multiline}
              onBlur={(ev) => handleBlur(ev)}
              onChange={(ev) => handleChange(ev)}
              onKeyPress={isSubmitOnEnter ? (ev) => handleKeyPress(ev) : undefined}
              ref={inputRef}
              value={content}
            />
          </>
        )}
        {!editing && !renderHtml && (
          <PresEditorTooltip disableWrapper title={isTruncated ? content : tooltipTitle ? tooltipTitle : ''}>
            <div className={classnames(classes.editableBlock, { [classes.height]: height })}>
              {/* the ref/overflow-clipping live on this span, not Typography — Typography here
                  isn't a forwardRef component, so a ref on it would silently attach to nothing */}
              <span ref={truncateRef} className={classes.typogText}>
                <Typography component="span" variant={variant as any}>
                  {content}
                </Typography>
              </span>
              {hasIcon && <CreateIcon className={classes.editableIcon} />}
            </div>
          </PresEditorTooltip>
        )}
        {!editing && renderHtml && <HtmlContent content={content} />}
        {!editing && !content && <Placeholder Icon={CodeIcon} buttonText={placeholderButtonText} />}
      </div>
    </>
  )
}
